/**
 * Protected Routes Layout
 *
 * This pathless layout route centralizes strict authentication validation.
 * It allows shell components to render BEFORE auth checks happen on protected routes.
 *
 * All routes under the _protected/ directory inherit this authentication check.
 * Public routes (login, logout, error, forbidden) remain at the root level.
 *
 * ## Defense in Depth
 * Authentication is validated in two places:
 * 1. `beforeLoad` - blocks navigation until auth is validated (primary)
 * 2. `ProtectedComponent` - shows spinner until conditions are met (fallback)
 *
 * ## Infinite Spinner Prevention
 * The component includes a timeout (COMPONENT_VALIDATION_TIMEOUT_MS) that redirects
 * to the error page if validation doesn't complete. This prevents infinite spinner in
 * edge cases like:
 * - Race conditions between beforeLoad completion and component state
 * - Unexpected cache invalidation
 * - Network issues after beforeLoad passed
 */

import { useState, useEffect, useCallback } from 'react';
import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import AppAuth from 'AppAuth';
import { checkAuthorization } from 'lib/AuthorizedLib';
import AppConfig from 'appConfig';
import { ZYW_LEADS_LOGOUT_REDIRECT, USER_TYPE_ZYWAVE } from 'lib/config/appConstants';
import { isZywaveUrl } from 'lib/AppHelpers';
import MiZuiSpinner from 'components/common/MiZuiSpinner';
import { useZywaveFeatures } from 'hooks/useZywaveFeatures';

// Cache for Zywave proxy validation (5 minute TTL)
const ZYWAVE_VALIDATION_CACHE_MS = 5 * 60 * 1000;
const ZYWAVE_VALIDATION_TIMEOUT_MS = 10 * 1000;
let zywaveValidationCache: { timestamp: number; status: number } | null = null;

// Timeout for component-level validation to prevent infinite spinner
// This is a safety net - normally validation completes immediately since beforeLoad already ran
const COMPONENT_VALIDATION_TIMEOUT_MS = 15 * 1000;

/**
 * Validates that Zywave proxy is reachable for Zywave users.
 * Returns the HTTP status code, or 0 for network errors/timeouts.
 * Results are cached for 5 minutes to reduce API calls.
 */
async function validateZywaveProxy(): Promise<number> {
  // Check cache first
  if (
    zywaveValidationCache &&
    Date.now() - zywaveValidationCache.timestamp < ZYWAVE_VALIDATION_CACHE_MS
  ) {
    return zywaveValidationCache.status;
  }

  // Wait for Zywave components to be ready
  await customElements.whenDefined('zywave-shell');
  await customElements.whenDefined('zywave-api-proxy');

  const maxRetries = 3;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ZYWAVE_VALIDATION_TIMEOUT_MS);

    try {
      const response = await fetch(`${AppConfig.zywaveProxyUrl}userinfo`, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.status === 200) {
        zywaveValidationCache = { timestamp: Date.now(), status: response.status };
        return response.status;
      }

      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
        continue;
      }
      return response.status;
    } catch {
      clearTimeout(timeoutId);
      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
        continue;
      }
      return 0;
    }
  }
  return 0;
}

/**
 * Checks if Zywave proxy validation cache is valid (status 200 and not expired).
 * This is a synchronous check for the component to use.
 */
function isZywaveProxyCacheValid(): boolean {
  return (
    zywaveValidationCache !== null &&
    zywaveValidationCache.status === 200 &&
    Date.now() - zywaveValidationCache.timestamp < ZYWAVE_VALIDATION_CACHE_MS
  );
}

/**
 * Clears the Zywave validation cache. Call this on logout or error.
 */
export function clearProtectedValidationCache(): void {
  zywaveValidationCache = null;
}

/**
 * beforeLoad hook for protected routes.
 * Handles synchronous checks only - no Zywave API calls here.
 * Zywave API calls are deferred to the component's useEffect to ensure
 * shell components are fully rendered before making API requests.
 *
 * 1. Authentication check (synchronous localStorage check)
 * 2. Authorization check (synchronous)
 *
 * Zywave proxy validation is handled in ProtectedComponent's useEffect.
 */
function protectedBeforeLoad() {
  // 1. Check if logged in (synchronous - just checks localStorage)
  if (!AppAuth.loggedIn()) {
    localStorage.setItem(
      ZYW_LEADS_LOGOUT_REDIRECT,
      window.location.pathname + window.location.search + window.location.hash
    );
    throw redirect({ to: '/login' });
  }

  // 2. Clear redirect tracking and check authorization
  if (AppConfig.enableRedirectZywaveDotCom) {
    AppAuth.clearRedirectTracking();
  }

  const authorizedObj = checkAuthorization();
  if (!authorizedObj.isAuthorized) {
    throw redirect({
      to: '/forbidden',
      search: { homePathUri: authorizedObj.homePathUri },
    });
  }

  return { isAuthenticated: true, isAuthorized: true };
}

/**
 * Protected component that validates auth conditions before rendering children.
 *
 * All Zywave API calls are made in useEffect to ensure shell components are
 * fully rendered before making requests. This prevents timing issues where
 * API calls fail because ZUI web components aren't ready yet.
 *
 * Shows a spinner while:
 * 1. Validating user is logged in
 * 2. For Zywave users: validating proxy and loading features
 *
 * Includes timeout protection to prevent infinite spinner - if validation
 * doesn't complete within COMPONENT_VALIDATION_TIMEOUT_MS, redirects to error page.
 */
function ProtectedComponent() {
  const navigate = useNavigate();
  const [isValidated, setIsValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  // State for Zywave user info - populated after validation completes
  const [zywaveUserInfo, setZywaveUserInfo] = useState<{
    userId: string | undefined;
    accountType: number | undefined;
    loaded: boolean;
  }>({ userId: undefined, accountType: undefined, loaded: false });

  // Load Zywave user info from localStorage when validation completes
  // This ensures useZywaveFeatures only starts fetching AFTER validation passes
  useEffect(() => {
    if (!isValidated) {
      return;
    }

    const userInfoStr = localStorage.getItem('user.info');
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        setZywaveUserInfo({
          userId: userInfo.id?.toString(),
          accountType: userInfo.account_type,
          loaded: true,
        });
      } catch {
        // Parse error - mark as loaded with undefined values
        setZywaveUserInfo({ userId: undefined, accountType: undefined, loaded: true });
      }
    } else {
      // No user info - mark as loaded
      setZywaveUserInfo({ userId: undefined, accountType: undefined, loaded: true });
    }
  }, [isValidated]);

  const isZywaveUser = zywaveUserInfo.accountType === USER_TYPE_ZYWAVE && isZywaveUrl();

  // useZywaveFeatures only runs AFTER validation passes AND user info is loaded
  // This ensures API calls happen after initial render when ZUI components are ready
  const zywaveFeatures = useZywaveFeatures(zywaveUserInfo.userId, zywaveUserInfo.accountType);

  // For Zywave users, check if features are loaded
  // Allow render even on error to avoid blocking the app
  const featuresReady = !zywaveUserInfo.loaded
    ? false // Still loading user info
    : isZywaveUser
      ? zywaveFeatures?.isSuccess || zywaveFeatures?.isError
      : true; // Non-Zywave users don't need features

  const validateAndRender = useCallback(async () => {
    // Check 1: User must be logged in
    if (!AppAuth.loggedIn()) {
      // This shouldn't happen since beforeLoad already validated
      // But if it does, redirect to login
      localStorage.setItem(
        ZYW_LEADS_LOGOUT_REDIRECT,
        window.location.pathname + window.location.search + window.location.hash
      );
      navigate({ to: '/login' });
      return;
    }

    // Check 2: For Zywave users, validate proxy cache
    const userInfoStr = localStorage.getItem('user.info');
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo.account_type === USER_TYPE_ZYWAVE) {
          // If cache is valid from a previous navigation, skip re-validation
          if (isZywaveProxyCacheValid()) {
            setIsValidated(true);
            setIsValidating(false);
            return;
          }

          // Validate Zywave proxy - this API call happens in useEffect
          // to ensure shell components are fully rendered first
          const status = await validateZywaveProxy();
          if (status === 401) {
            localStorage.setItem(
              ZYW_LEADS_LOGOUT_REDIRECT,
              window.location.pathname + window.location.search + window.location.hash
            );
            navigate({ to: '/login' });
            return;
          }
          if (status !== 200) {
            const errorCode = [400, 403, 404, 500].includes(status) ? status : 500;
            navigate({
              to: '/error',
              search: { statusCode: String(errorCode) as '400' | '403' | '404' | '500' },
            });
            return;
          }
        }
      } catch {
        navigate({ to: '/error', search: { statusCode: '500' } });
        return;
      }
    }

    // All checks passed
    setIsValidated(true);
    setIsValidating(false);
  }, [navigate]);

  useEffect(() => {
    // Start validation
    validateAndRender();

    // Safety timeout to prevent infinite spinner
    // If validation doesn't complete in time, redirect to error
    const timeoutId = setTimeout(() => {
      if (!isValidated) {
        console.error(
          '[_protected] Component validation timeout - redirecting to error page. ' +
            'This indicates a potential race condition or auth state inconsistency.'
        );
        navigate({ to: '/error', search: { statusCode: '500' } });
      }
    }, COMPONENT_VALIDATION_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [validateAndRender, isValidated, navigate]);

  // Show spinner while:
  // 1. Still validating auth/proxy
  // 2. For Zywave users: features not yet loaded
  if (isValidating || !isValidated || !featuresReady) {
    return <MiZuiSpinner />;
  }

  // Validation passed and features loaded - render child routes
  return <Outlet />;
}

export const Route = createFileRoute('/_protected')({
  beforeLoad: protectedBeforeLoad,
  component: ProtectedComponent,
});
